import * as moment from 'moment';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { signOut as signOutAction } from "../../actions/loginActions";
import {
  checkUncheck as checkUncheckAction, clearMailData as clearMailDataAction, refreshList as refreshListAction, savePaginationInfo as savePaginationInfoAction, selectMessage as selectMessageAction,
  setDeleteEditMode as setDeleteEditModeAction, setReleaseEditMode as setReleaseEditModeAction, setSortFieldAndOrder as setSortFieldAndOrderAction
} from "../../actions/mailActions";
import { addNotification as addNotificationAction, clearNotifications as clearNotificationsAction } from "../../actions/notificationsActions";
import { MailService } from '../../services/mail';
import Notification from '../common/Notification';
import UserSettings from '../settings/model/Settings';
import Message from './model/Message';
import Paging from './model/Paging';

interface INavBarProps {
  csrf: string;
  loginTime: number;
  messageList: Message[];
  selectedMessage: Message;
  user: string;
  wsid: string;
  sortField: string;
  isDesc: boolean;
  windowWidth: number;
  settings: UserSettings;
  releaseMode: boolean;
  deleteMode: boolean;
  addNotification: (notification: Notification) => any;
  clearMailData: () => any;
  clearNotifications: () => any;
  checkUncheck: (messages: Message[]) => any;
  refreshList: (messages: Message[]) => any;
  savePaginationInfo: (paginationInfo: Paging) => any;
  selectMessage: (messages: Message) => any;
  setSortFieldAndOrder: (sortField: string, isDesc: boolean) => any;
  setDeleteEditMode: (isDelete: boolean) => any;
  setReleaseEditMode: (isRelease: boolean) => any;
  signOut: () => any;
}

interface INavBarState {
  selectAll: boolean;
  releaseLoading: boolean;
  releaseSuccess: boolean;
  deleteLoading: boolean;
  deleteSuccess: boolean;
}

export class NavBar extends React.Component<INavBarProps, INavBarState> {
  public mailService: MailService;
  public interval: any;
  public timeoutIds: number[];

  constructor(props: INavBarProps) {
    super(props);
    this.mailService = new MailService(this.props.user, this.props.wsid, this.props.csrf);
    this.releaseMail = this.releaseMail.bind(this);
    this.deleteMail = this.deleteMail.bind(this);
    this.refreshMail = this.refreshMail.bind(this);
    this.sortMessages = this.sortMessages.bind(this);
    this.toggleRelease = this.toggleRelease.bind(this);
    this.toggleDelete = this.toggleDelete.bind(this);
    this.handleError = this.handleError.bind(this);
    this.timeoutIds = [];

    this.state = {
      selectAll: false, releaseLoading: false, releaseSuccess: false, deleteLoading: false, deleteSuccess: false
    };
  }

  public toggleRelease() {
    this.props.setReleaseEditMode(!this.props.releaseMode);
  }

  public toggleDelete() {
    this.props.setDeleteEditMode(!this.props.deleteMode);
  }

  public async releaseMail() {
    const selectedMail = this.props.messageList ? this.props.messageList.filter((m) => m.selected) : [];
    try {
      if (selectedMail.length === 0) { // if nothing is selected, add currently previewed message
        selectedMail.push(this.props.selectedMessage);
      }

      this.setState({ releaseLoading: true });
      const promises = selectedMail.map(mail => this.mailService.release(mail.id));
      const resolved = await Promise.all(promises);
      const errors = resolved.filter(response => !response.ok).map(response => response.status.toString());
      if (errors.length > 0) {
        throw new Error(errors.join('\n'));
      }

      this.props.setReleaseEditMode(false);
      this.setState({ releaseLoading: false, releaseSuccess: true });

      this.timeoutIds.push(setTimeout((() => {
        this.setState({ releaseSuccess: false })
      }).bind(this), 2000));

      this.refreshMail(true);
    } catch (e) {
      this.setState({ releaseLoading: false, releaseSuccess: false });
      this.handleError(e.message === '401', "L.Error.Release");
    }
  }

  public async deleteMail() {
    const selectedMail = this.props.messageList ? this.props.messageList.filter((m) => m.selected) : [];
    try {
      if (selectedMail.length === 0) { // if nothing is selected, add currently previewed message
        selectedMail.push(this.props.selectedMessage);
      }

      this.setState({ deleteLoading: true });
      const promises = selectedMail.map(mail => this.mailService.delete(mail.id));
      const resolved = await Promise.all(promises);
      const errors = resolved.filter(response => !response.ok).map(response => response.status.toString());
      if (errors.length > 0) {
        throw new Error(errors.join('\n'));
      }

      this.props.setDeleteEditMode(false);
      this.setState({ deleteLoading: false, deleteSuccess: true });

      this.timeoutIds.push(setTimeout((() => {
        this.setState({ deleteSuccess: false })
      }).bind(this), 2000));
      this.refreshMail(true);
    } catch (e) {
      this.setState({ deleteLoading: false, deleteSuccess: false });
      this.handleError(e.message === '401', "L.Error.Remove");
    }
  }

  public async refreshMail(selectFirstMsg: boolean) {
    try {
      const response = await this.mailService.list(0, 50, 'desc', this.props.sortField);

      if (!response.ok) {
        throw new Error(response.status.toString());
      }

      const msgList = await response.json();
      if (selectFirstMsg) {
        this.props.selectMessage(msgList.messages.length > 0 ? msgList.messages[0] : {});
      }

      this.props.refreshList(msgList.messages);
      this.props.savePaginationInfo(msgList.paging); // save pagination info

      this.setState({ selectAll: false });
    } catch (e) {
      this.handleError(e.message === '401', "L.Error.Retrieve");
    }
  }

  public handleSelectAll() {
    if (this.props.messageList) {
      let anyUnselected = false;

      for (const m of this.props.messageList) {
        if (!m.selected) {
          anyUnselected = true;
          break;
        }
      }

      const selectedMessages = this.props.messageList.map((m) => {
        return { ...m, selected: anyUnselected ? true : !this.state.selectAll ? true : false };
      })
      this.props.checkUncheck(selectedMessages);
    }
    this.setState({ selectAll: !this.state.selectAll });
  }

  public async sortMessages(e: React.FormEvent<HTMLSelectElement>) {
    const sortField = e.currentTarget.value;

    try {
      const response = await this.mailService.list(0, 50, this.props.isDesc ? 'asc' : 'desc', sortField);

      if (!response.ok) {
        throw new Error(response.status.toString());
      }

      const msgList = await response.json();
      this.props.refreshList(msgList.messages);
      this.props.selectMessage(msgList.messages[0]); // select first message by default
      this.props.setSortFieldAndOrder(sortField, !this.props.isDesc);
    } catch (e) {
      this.handleError(e.message === '401', "L.Error.Retrieve");
    }
  }

  public handleError(isSessionTimeout: boolean, errorMessageKey: string) {
    if (isSessionTimeout) {
      if (moment().diff(this.props.loginTime, 'hours') < 12) {
        this.props.addNotification({ message: "Session Timeout", isError: true });
      }
      this.props.signOut();
      this.props.clearMailData();
    } else {
      this.props.addNotification({ messageKey: errorMessageKey, isError: true });
    }
  }

  public componentDidMount() {
    // refresh mail every 5 min
    this.interval = setInterval(() => this.refreshMail(false), 300000);
    this.props.setReleaseEditMode(false);
    this.props.setDeleteEditMode(false);
  }

  public componentWillUnmount() {
    clearInterval(this.interval);
    this.timeoutIds.forEach((id) => clearTimeout(id));
  }

  public render() {
    const releaseIcon = this.state.releaseLoading ? 'cached' : this.state.releaseSuccess ? 'check_circle' : 'reply';
    const deleteIcon = this.state.deleteLoading ? 'cached' : this.state.deleteSuccess ? 'check_circle' : 'delete_forever';
    const hasMessages = this.props.messageList && this.props.messageList.length > 0;
    const hasSelectedMessages = hasMessages ? this.props.messageList.filter((m) => m.selected).length > 0 : false;
    const disableActions = this.state.releaseLoading || this.state.deleteLoading;

    return (
      <div>
        <div className="email-item ei-toolbar sidebar">
          <div className="column">
            <div className="row align-items-center justify-content-between">
              <div className="col-7 pr-0 pl-1">
                {!this.props.deleteMode &&
                  <button type="button" id="releaseButton" className={"btn btn-primary " + (this.props.releaseMode ? hasSelectedMessages ? "refresh-all" : "refresh release-active" : !hasSelectedMessages ? "refresh release" : '')}
                    onClick={this.props.releaseMode && hasSelectedMessages ? this.releaseMail : this.toggleRelease}>
                    <i className={'material-icons ' + (releaseIcon !== 'check_circle' ? 'flip' : '')}>
                      {releaseIcon}
                    </i>
                    {this.props.windowWidth >= 503 && !this.state.releaseSuccess && <span id="release-text">
                      <FormattedMessage id="L.Release" defaultMessage="Release" />
                    </span>}
                  </button>}

                {!this.props.releaseMode &&
                  <button type="button" id="deleteButton" className={"btn btn-primary " + (this.props.deleteMode ? hasSelectedMessages ? "refresh-all" : "remove remove-active" : !hasSelectedMessages ? "refresh remove" : '')}
                    onClick={this.props.deleteMode && hasSelectedMessages ? this.deleteMail : this.toggleDelete}>
                    <i className="material-icons">
                      {deleteIcon}
                    </i>
                    {this.props.windowWidth >= 503 && !this.state.deleteSuccess && <span id="remove-text">
                      <FormattedMessage id="L.Remove" defaultMessage="Remove" />
                    </span>}
                  </button>}

                {!this.props.releaseMode && !this.props.deleteMode && <button type="button" id="refreshButton" className="btn btn-primary refresh" onClick={() => this.refreshMail(false)}>
                  <i className="material-icons">
                    refresh
                  </i>
                </button>}
              </div>
              {!this.props.releaseMode && !this.props.deleteMode && <div className="col-5 px-0 text-right">
                {this.props.windowWidth >= 503 && <label className="sort-label"><FormattedMessage id="L.Sort" defaultMessage="Sort By" /></label>}
                <select id="sortField" value={this.props.sortField} onChange={this.sortMessages}>
                  <option value="to"><FormattedMessage id="L.To" defaultMessage="To" /></option>
                  <option value="from"><FormattedMessage id="L.From" defaultMessage="From" /></option>
                  <option value="date"><FormattedMessage id="L.Sent" defaultMessage="Date" /></option>
                  <option value="subject"><FormattedMessage id="L.Subject" defaultMessage="Subject" /></option>
                </select>
              </div>}
            </div>
          </div>
        </div>
        <div className="row align-items-center desktop">
          <div className="ei-toolbar col-12 px-0">
            <button type="button" id="releaseButtonDesktop" className="btn btn-primary refresh release" disabled={disableActions || !hasMessages} onClick={this.releaseMail}>
              <i className={'material-icons ' + (releaseIcon !== 'check_circle' ? 'flip' : '')}>{releaseIcon}</i>
              {!this.state.releaseSuccess && <FormattedMessage id="L.Release" defaultMessage="Release" />}
            </button>

            <button type="button" id="deleteButtonDesktop" className="btn btn-primary refresh remove" disabled={disableActions || !hasMessages} onClick={this.deleteMail}>
              <i className="material-icons">{deleteIcon}</i>
              {!this.state.deleteSuccess && <FormattedMessage id="L.Remove" defaultMessage="Remove" />}
            </button>

            <button type="button" id="refreshButtonDesktop" className="btn btn-primary refresh" disabled={disableActions} onClick={() => this.refreshMail(false)}>
              <i className="material-icons">
                refresh
              </i>
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    csrf: state.authentication.csrf,
    deleteMode: state.mail.deleteMode,
    isDesc: state.mail.isDesc,
    loginTime: state.authentication.loginTime,
    messageList: state.mail.messageList,
    releaseMode: state.mail.releaseMode,
    selectedMessage: state.mail.selectedMessage,
    settings: state.settings.settings,
    sortField: state.mail.sortField,
    user: state.authentication.user,
    wsid: state.authentication.wsid,
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    addNotification: bindActionCreators(addNotificationAction, dispatch),
    clearMailData: bindActionCreators(clearMailDataAction, dispatch),
    clearNotifications: bindActionCreators(clearNotificationsAction, dispatch),
    checkUncheck: bindActionCreators(checkUncheckAction, dispatch),
    refreshList: bindActionCreators(refreshListAction, dispatch),
    savePaginationInfo: bindActionCreators(savePaginationInfoAction, dispatch),
    selectMessage: bindActionCreators(selectMessageAction, dispatch),
    setSortFieldAndOrder: bindActionCreators(setSortFieldAndOrderAction, dispatch),
    setDeleteEditMode: bindActionCreators(setDeleteEditModeAction, dispatch),
    setReleaseEditMode: bindActionCreators(setReleaseEditModeAction, dispatch),
    signOut: bindActionCreators(signOutAction, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NavBar);



// WEBPACK FOOTER //
// ./src/components/mail/NavBar.tsx